import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {z} from 'zod';
import {ENGINEER_FAVICON_PATH} from '../../../../config/constants/img';
import type {EngineerProblemDetailType} from '../../../../core/entities/company/schema';
import {getEngineerProblemDetail} from '../../../../core/repositories/engineerProblemRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {EngineerDrillPage} from '../../../../features/engineer/components/EngineerDrillPage';
import {withHandler} from '../../../../server/token/withHandler';
import {ScriptForGA} from '../../../../shared/components/ScriptForGA';
import {EngineerPageLayout} from '../../../../shared/components/layouts/EngineerPageLayout';
import type {NextPageWithLayout} from '../../../_app';

type Props = {
  problem?: EngineerProblemDetailType;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};
const QueryParameter = z.object({
  id: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {},
      };
    }

    const {id} = context.validatedQuery;
    const problem = await getEngineerProblemDetail({
      problemId: id,
    });

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {},
      };
    }

    return {
      props: {
        problem: problem.ok ? problem.value : undefined,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  problem,
  userDataForAnalytics,
}: Props) => {
  return (
    <>
      <Head>
        <link rel="icon" href={`${ENGINEER_FAVICON_PATH}/favicon.ico`} />
        <link
          rel="apple-touch-icon"
          type="image/png"
          sizes="180x180"
          href={`${ENGINEER_FAVICON_PATH}/apple-touch-icon.png`}
        />
        <link
          rel="manifest"
          href={`${ENGINEER_FAVICON_PATH}/manifest.webmanifest`}
        />
      </Head>
      {problem ? <EngineerDrillPage problem={problem} /> : undefined}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => <EngineerPageLayout>{page}</EngineerPageLayout>;
